/**
 * HomeScreenSkeleton — content-shaped loading placeholder for the Home (Learn)
 * tab. Web port of iOS client-native HomeScreenSkeleton.tsx: a sky-gradient hero
 * with pulsing week-strip circles, a translucent listening ring, a disabled
 * Continue CTA, then the white drawer peek with a course-card + chapter-card
 * skeleton. Rendered by LearnPage while userInfo/userStats load, replacing the
 * generic spinner so the first paint matches the real layout (iOS parity).
 *
 * The shell top bar (streak pill / logo / words badge) is owned by HomeLayout's
 * fixed header, so — unlike the iOS skeleton — this one starts below the 64px
 * header at the week strip and does NOT re-draw the top badges.
 */
import { CSSProperties, FC, ReactNode } from "react";
import { colors } from "@/ui";

// iOS SKELETON_GRADIENT (lighter than the live dark home sky) so the skeleton
// reads as a loading state, not the loaded screen.
const SKELETON_GRADIENT =
  "linear-gradient(180deg, #032B54 0%, #0757A0 25%, #1A7BD4 50%, #4DA8ED 75%, #8CCAF5 100%)";

/** Pulsing placeholder box (iOS PulseBox: opacity 0.25↔0.8, staggered). */
const PulseBox: FC<{
  width: number | string;
  height: number;
  radius?: number;
  delay?: number;
  style?: CSSProperties;
}> = ({ width, height, radius = 8, delay = 0, style }) => (
  <div
    className="skeleton-pulse"
    style={{
      width,
      height,
      borderRadius: radius,
      background: "rgba(255,255,255,0.2)",
      animationDelay: `${delay}ms`,
      ...style,
    }}
  />
);

/** Soft pulsing wrapper for whole cards (iOS PulseWrap: opacity 0.5↔1). */
const PulseWrap: FC<{ delay?: number; children: ReactNode; style?: CSSProperties }> = ({
  delay = 0,
  children,
  style,
}) => (
  <div
    className="skeleton-pulse-soft"
    style={{ animationDelay: `${delay}ms`, ...style }}
  >
    {children}
  </div>
);

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

const HomeScreenSkeleton: FC = () => (
  <div style={{ position: "relative", width: "100%", height: "100%" }}>
    {/* Sky gradient backdrop (full-bleed, like SkyBackground). */}
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        background: SKELETON_GRADIENT,
        zIndex: 0,
      }}
    />

    <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
      <div
        style={{
          width: "100%",
          maxWidth: 600,
          margin: "0 auto",
          padding: "64px 0 0",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Hero on the sky: week strip + ring + Continue. */}
        <div
          style={{
            minHeight: "calc(67vh - 64px)",
            display: "flex",
            flexDirection: "column",
            padding: "0 16px",
          }}
        >
          {/* Week strip */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-around",
              padding: "0 4px",
              marginTop: 8,
              marginBottom: 14,
            }}
          >
            {DAYS.map((label, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  flex: 1,
                }}
              >
                <span
                  style={{
                    fontSize: "var(--base-font-size-xs)",
                    fontWeight: 600,
                    color: colors.white,
                    opacity: 0.35,
                  }}
                >
                  {label}
                </span>
                <PulseBox width={34} height={34} radius={17} delay={100 + idx * 80} />
              </div>
            ))}
          </div>

          {/* Ring + Continue fill the rest (space-evenly, like LearnPage). */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            {/* Listening ring skeleton — matches ListeningTimeRing shell. */}
            <div
              className="skeleton-pulse-soft"
              style={{
                width: 168,
                height: 168,
                maxWidth: "70vw",
                aspectRatio: "1 / 1",
                borderRadius: "50%",
                border: `6px solid ${colors.whiteBold}`,
                background: "rgba(255,255,255,0.08)",
                boxShadow: "0 0 24px rgba(255,255,255,0.25)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                animationDelay: "300ms",
              }}
            >
              <PulseBox width={60} height={22} radius={6} delay={400} />
              <PulseBox
                width={80}
                height={12}
                radius={4}
                delay={500}
                style={{ marginTop: 6 }}
              />
            </div>

            {/* Disabled Continue CTA */}
            <PulseWrap delay={650}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "14px 50px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.45)",
                  color: "rgba(9,102,191,0.35)",
                  fontSize: "var(--base-font-size-large)",
                  fontWeight: 700,
                }}
              >
                <span aria-hidden>▶</span> Continue
              </div>
            </PulseWrap>
          </div>
        </div>

        {/* White drawer peek with course + chapter card skeletons. */}
        <div
          style={{
            position: "relative",
            marginTop: -24,
            background: "var(--color-bg)",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            boxShadow: "0 -8px 16px rgba(0,0,0,0.12)",
            padding: "20px 16px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            minHeight: "38vh",
          }}
        >
          {/* Course card skeleton (purple gradient shell). */}
          <PulseWrap delay={600}>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 120,
                borderRadius: 20,
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, #D5CDE6 0%, #C8BFD9 55%, #BDB3CF 100%)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: 20,
                boxSizing: "border-box",
              }}
            >
              <PulseBox
                width={100}
                height={12}
                radius={4}
                delay={700}
                style={{ background: "rgba(255,255,255,0.3)" }}
              />
              <PulseBox
                width={160}
                height={20}
                radius={6}
                delay={800}
                style={{ marginTop: 8, background: "rgba(255,255,255,0.35)" }}
              />
            </div>
          </PulseWrap>

          {/* Chapter card skeleton (dark gradient, play glyph). */}
          <PulseWrap delay={1000}>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 120,
                borderRadius: 16,
                overflow: "hidden",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 36,
                  color: "rgba(255,255,255,0.4)",
                }}
              >
                ▶
              </div>
              <div style={{ position: "absolute", bottom: 12, left: 14 }}>
                <PulseBox
                  width={70}
                  height={10}
                  radius={3}
                  delay={1100}
                  style={{ background: "rgba(255,255,255,0.25)" }}
                />
                <PulseBox
                  width={140}
                  height={16}
                  radius={5}
                  delay={1200}
                  style={{ marginTop: 6, background: "rgba(255,255,255,0.3)" }}
                />
              </div>
            </div>
          </PulseWrap>
        </div>
      </div>
    </div>
  </div>
);

export default HomeScreenSkeleton;
